import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Copy, Trash2 } from "lucide-react";

const templates = {
  Monday: { key: "before_after", label: "Before & After" },
  Wednesday: { key: "sewing_tip", label: "Sewing Tip" },
  Friday: { key: "customer_transformation", label: "Customer Transformation" },
  Sunday: { key: "special_offer", label: "Special Offer" },
};

export default function AdminContent() {
  const [posts, setPosts] = useState([]);
  const [hashtags, setHashtags] = useState({});
  const [form, setForm] = useState({
    day: "Monday", template: "before_after", caption: "",
    hashtags: ["#SydneyTailor", "#BeforeAndAfter"],
    scheduled_for: "", status: "draft"
  });

  const load = async () => {
    const [{ data: p }, { data: h }] = await Promise.all([api.get("/admin/posts"), api.get("/admin/hashtags")]);
    setPosts(p); setHashtags(h);
  };
  useEffect(() => { load(); }, []);

  const setDay = (day) => {
    setForm({ ...form, day, template: templates[day].key });
  };

  const save = async () => {
    try {
      const payload = { ...form, scheduled_for: form.scheduled_for || new Date().toISOString() };
      await api.post("/admin/posts", payload);
      toast.success("Post saved. Copy caption + hashtags to publish manually on IG/FB.");
      setForm({ ...form, caption: "" });
      await load();
    } catch { toast.error("Failed"); }
  };

  const copyCaption = (p) => {
    const text = `${p.caption}\n\n${(p.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const del = async (id) => { await api.delete(`/admin/posts/${id}`); await load(); };

  const toggleHashtag = (tag) => {
    setForm({ ...form,
      hashtags: form.hashtags.includes(tag)
        ? form.hashtags.filter(t => t !== tag)
        : [...form.hashtags, tag]
    });
  };

  return (
    <div className="p-8 md:p-12">
      <div className="overline">Admin</div>
      <h1 className="font-serif-display text-4xl mt-2">Content Calendar</h1>
      <p className="text-[#4A4A4A] mt-3 text-sm">Weekly template: Mon · Before &amp; After · Wed · Sewing Tip · Fri · Customer Transformation · Sun · Special Offer</p>

      <div className="mt-10 grid md:grid-cols-4 gap-4">
        {Object.entries(templates).map(([day, t]) => (
          <button key={day} data-testid={day === form.day ? ADMIN.postDay : `day-${day}`} onClick={() => setDay(day)}
                  className={`p-6 text-left border ${form.day === day ? "border-[#0F172A] bg-[#F8FAFC]" : "border-[#E2E8F0] bg-white hover:border-[#0F172A]"}`}>
            <div className="overline">{day}</div>
            <div className="font-serif-display text-xl mt-2">{t.label}</div>
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 border border-[#E2E8F0] bg-white p-6">
          <div className="overline">Caption</div>
          <textarea data-testid={ADMIN.postCaption} rows={6} value={form.caption}
                    onChange={(e) => setForm({ ...form, caption: e.target.value })}
                    placeholder={`Write the ${templates[form.day].label.toLowerCase()} caption for ${form.day}...`}
                    className="mt-2 w-full border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-3 outline-none"/>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label className="overline">Schedule for</label>
              <input type="datetime-local"
                     onChange={(e) => setForm({ ...form, scheduled_for: new Date(e.target.value).toISOString() })}
                     className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-2 outline-none"/>
            </div>
            <div>
              <label className="overline">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-2 outline-none">
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <button data-testid={ADMIN.postSave} onClick={save} disabled={!form.caption}
                  className="mt-6 px-6 py-3 bg-[#D4AF37] text-[#0F172A] text-xs uppercase tracking-[0.25em] hover:bg-[#B8941F] disabled:opacity-30">
            Save Post
          </button>
        </div>

        <div>
          <div className="overline mb-4">Hashtag library</div>
          {["sydney", "craft"].map(cat => (
            <div key={cat} className="mb-6">
              <div className="text-xs uppercase font-medium text-[#4A4A4A] mb-2">{cat}</div>
              <div className="flex flex-wrap gap-2">
                {(hashtags[cat] || []).map(tag => (
                  <button key={tag} onClick={() => toggleHashtag(tag)}
                          className={`text-xs px-2 py-1 border ${form.hashtags.includes(tag) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0] hover:border-[#0F172A]"}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {hashtags.templates?.[form.template] && (
            <div>
              <div className="text-xs uppercase font-medium text-[#4A4A4A] mb-2">This template</div>
              <div className="flex flex-wrap gap-2">
                {hashtags.templates[form.template].map(tag => (
                  <button key={tag} onClick={() => toggleHashtag(tag)}
                          className={`text-xs px-2 py-1 border ${form.hashtags.includes(tag) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0] hover:border-[#0F172A]"}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <h2 className="font-serif-display text-3xl mt-16">Scheduled Posts</h2>
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {posts.map(p => (
          <div key={p.id} className="border border-[#E2E8F0] bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="overline">{p.day} · {(templates[p.day] || {}).label || p.template}</div>
              <span className="text-xs uppercase px-2 py-1 bg-[#F8FAFC]">{p.status}</span>
            </div>
            <p className="mt-3 text-sm whitespace-pre-line">{p.caption}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(p.hashtags || []).map(t => <span key={t} className="text-xs text-[#0F172A]">{t}</span>)}
            </div>
            <div className="mt-4 flex gap-3">
              <button onClick={() => copyCaption(p)} className="text-xs uppercase tracking-widest link-underline text-[#0F172A]">
                <Copy size={12} className="inline mr-1"/> Copy
              </button>
              <button onClick={() => del(p.id)} className="text-xs uppercase tracking-widest text-[#0F172A] link-underline">
                <Trash2 size={12} className="inline mr-1"/> Delete
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="text-sm text-[#4A4A4A]">No posts yet.</div>}
      </div>
    </div>
  );
}
